import {
  AuxiliaryBoardFile,
  Board,
  boardHasBeenPaidFor,
  FileProcessingState,
  ManufacturingOutput,
  PartShoppingListStatus,
  Shipment,
} from "../../../reducers/uploadedBoard";
import Link from "next/link";
import {
  FakeLink,
  TableHeaderCell,
  TableHeaderRow,
} from "../../../constants/Styles";
import useDownloadBOMFile from "../../../utils/hooks/commonly-stored/uploaded-board/bomFile";
import useDownloadTraveler from "../../../utils/hooks/commonly-stored/uploaded-board/travelerSticker";
import {
  Chevron,
  ExpandToggle,
  FileCell,
  FileRow,
  FilesTable,
} from "./BoardFilesStyles";
import useDownloadPartPickList from "../../../utils/hooks/commonly-stored/uploaded-board/partPickList";
import {
  dateStringToHumanReadableDate,
  datetimeStringToHumanReadableDatetime,
} from "../../../utils/valueFormatting";
import { UserIdPill } from "../../common/UserPill";
import { ReactNode, useContext, useState } from "react";
import Button from "../../common/Button";
import useUpdateShoppingList from "../../../utils/hooks/commonly-stored/uploaded-board/shoppingList";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import LoadingOverlay from "../../LoadingOverlay";
import LoadingIcon from "../../common/LoadingIcon";
import { ButtonVariants } from "../../common/Button/constants";
import useBoardFiles from "../../../utils/hooks/commonly-stored/uploaded-board/files";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import PanelDetails from "./PanelDetails";
import useShipmentsFromContext from "../../../utils/hooks/commonly-stored/uploaded-board/shipments";

interface FileVersion {
  key: string;
  download: ReactNode;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

// A group is one kind of file. Its additional files (e.g. previous versions)
// are shown muted under its rows, collapsed when there are many of them.
interface FileGroup {
  title: string;
  rows: FileVersion[];
  additional?: FileVersion[];
  additionalLabel?: string;
  status?: ReactNode;
  action?: ReactNode;
}

const MAX_UNCOLLAPSED_ADDITIONAL_FILES = 2;

const AUXILIARY_FILE_TYPE_TITLES: Record<string, string> = {
  production_file: "Production file",
  inspection_report: "Inspection report",
};

function newestFirst(versions: FileVersion[]) {
  const time = (version: FileVersion) => {
    return version.updatedAt ? new Date(version.updatedAt).getTime() : 0;
  };
  return [...versions].sort((a, b) => {
    return time(b) - time(a);
  });
}

// The newest version leads the group and the rest are previous versions.
function versionedGroup(title: string, versions: FileVersion[]): FileGroup {
  const [latest, ...previous] = newestFirst(versions);
  return {
    title,
    rows: latest ? [latest] : [],
    additional: previous,
  };
}

function downloadLink(href: string, fileName: string) {
  return (
    <Link href={href} download={fileName}>
      {fileName}
    </Link>
  );
}

function auxiliaryFileVersion(file: AuxiliaryBoardFile): FileVersion {
  return {
    key: file.url,
    download: downloadLink(file.url, file.file_name),
    updatedAt: file.uploaded_at,
  };
}

function auxiliaryFileGroups(files: AuxiliaryBoardFile[]): FileGroup[] {
  const byType = new Map<string, AuxiliaryBoardFile[]>();
  const untyped: FileGroup[] = [];
  files.forEach((file) => {
    if (file.file_type === null) {
      untyped.push({
        title: "Auxiliary file",
        rows: [auxiliaryFileVersion(file)],
      });
      return;
    }
    byType.set(file.file_type, [...(byType.get(file.file_type) ?? []), file]);
  });
  const typed = [...byType.entries()].map(([fileType, typeFiles]) => {
    return versionedGroup(
      AUXILIARY_FILE_TYPE_TITLES[fileType] ?? fileType,
      typeFiles.map(auxiliaryFileVersion),
    );
  });
  return [...typed, ...untyped];
}

// Unprocessed means the file does not reflect the board: either it was never
// generated, or the board changed after it was.
function manufacturingOutputStatus(output: ManufacturingOutput) {
  if (output.state === FileProcessingState.PROCESSING) {
    return <LoadingIcon text={"Generating..."} />;
  }
  if (output.state === FileProcessingState.PROCESSED) {
    return "Processed";
  }
  if (output.file === null) {
    return "Not generated yet";
  }
  return "Out of date: the board has changed since this was generated";
}

export default function BoardFiles({ board }: { board: Board }) {
  const { shipmentId } = useContext(BoardIdContext) as BoardShipmentIds;
  const shipments = useShipmentsFromContext(board.id) as Shipment[];
  const downloadBOM = useDownloadBOMFile();
  const { boardFiles, refreshBoardFiles } = useBoardFiles(board.id);
  const { updateShoppingList, updating } =
    useUpdateShoppingList(refreshBoardFiles);
  // Generation goes through the board events endpoint; the machine only
  // accepts the event from FlashPCB engineers.
  const { sendBoardEvent, loading: creating } = useSendBoardEvent(
    undefined,
    () => {
      refreshBoardFiles();
    },
  );
  const createManufacturingOutput = async () => {
    await sendBoardEvent(board.id, {
      machine_group: MachineGroup.BOARD,
      model_id: board.id,
      event: BoardTransitionEvent.SHOULD_CREATE_MANUFACTURING_OUTPUT,
    });
  };
  const downloadTravelerSticker = useDownloadTraveler();
  const downloadPartPickList = useDownloadPartPickList();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (title: string) => {
    setExpandedGroups((expanded) => {
      const next = new Set(expanded);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  if (boardFiles === null) {
    return (
      <LoadingIcon
        text={"Loading files..."}
        centerHorizontally
        centerVertically
      />
    );
  }

  const isGeneratingManufacturingOutput =
    creating ||
    boardFiles.manufacturing_output.state === FileProcessingState.PROCESSING;
  const isShoppingListProcessing =
    boardFiles.part_shopping_lists?.status ===
    PartShoppingListStatus.PROCESSING;

  const singleFileVersion = (href: string, fileName: string): FileVersion => {
    return {
      key: href,
      download: downloadLink(href, fileName),
    };
  };
  const singleFile = (
    title: string,
    href: string | undefined,
    fileName: string,
  ): FileGroup[] => {
    return href ? [{ title, rows: [singleFileVersion(href, fileName)] }] : [];
  };

  const gerberUploads = boardFiles.gerber_uploads.map((upload) => {
    return {
      key: upload.gerber_zip.url,
      download: downloadLink(
        upload.gerber_zip.url,
        upload.gerber_zip.file_name,
      ),
      updatedAt: upload.uploaded_at,
      updatedBy: upload.uploaded_by,
    };
  });
  // A single upload is the latest unprocessed gerber, so uploads are only
  // worth listing once there is more than one.
  const gerberGroup: FileGroup = boardFiles.latest_unprocessed_gerber
    ? {
        title: "Latest unprocessed gerber",
        rows: [
          singleFileVersion(
            boardFiles.latest_unprocessed_gerber,
            `latest-unprocessed-gerber-${board.id}`,
          ),
        ],
        additional: gerberUploads.length > 1 ? newestFirst(gerberUploads) : [],
        additionalLabel: "Additional",
      }
    : versionedGroup("Gerber upload", gerberUploads);

  const shoppingListVersions: FileVersion[] = (
    boardFiles.part_shopping_lists?.lists ?? []
  ).flatMap((shoppingList) => {
    const shipment = shipments.find((shipment) => {
      return shipment.id == shoppingList.shipment_id;
    });
    if (shipment === undefined) {
      return [];
    }
    return [
      {
        key: shoppingList.shipment_id,
        download: (
          <Link href={shoppingList.spreadsheet_link}>
            {shipment.num_boards} boards -{" "}
            {dateStringToHumanReadableDate(shipment.ship_date as string)}
          </Link>
        ),
        updatedAt: boardFiles.part_shopping_lists.date_created,
      },
    ];
  });

  const groups: FileGroup[] = [
    ...singleFile("brd", boardFiles.brd, `brd-${board.id}`),
    ...singleFile("sch", boardFiles.sch, `sch-${board.id}`),
    ...singleFile("kicad_zip", boardFiles.kicad_zip, `pcb-${board.id}`),
    gerberGroup,
    ...singleFile(
      "DRC gerber",
      boardFiles.drc_output_gerber,
      `drc-gerber-${board.id}`,
    ),
    versionedGroup(
      "BOM upload",
      boardFiles.additional_bom_files.flatMap((fileSet) => {
        if (!fileSet.bom) {
          return [];
        }
        return [
          {
            key: fileSet.bom.url,
            download: downloadLink(fileSet.bom.url, fileSet.bom.file_name),
            updatedAt: fileSet.uploaded_at,
            updatedBy: fileSet.uploaded_by,
          },
        ];
      }),
    ),
    versionedGroup(
      "PnP upload",
      boardFiles.additional_bom_files.flatMap((fileSet) => {
        if (!fileSet.pnp) {
          return [];
        }
        return [
          {
            key: fileSet.pnp.url,
            download: downloadLink(fileSet.pnp.url, fileSet.pnp.file_name),
            updatedAt: fileSet.uploaded_at,
            updatedBy: fileSet.uploaded_by,
          },
        ];
      }),
    ),
    ...auxiliaryFileGroups(boardFiles.auxiliary_files),
    {
      title: "Manufacturing output",
      rows: [
        {
          key: "manufacturing-output",
          download:
            boardFiles.manufacturing_output.file === null ? null : (
              <Link href={boardFiles.manufacturing_output.file}>Download</Link>
            ),
          updatedAt: boardFiles.manufacturing_output.datetime_processed,
        },
      ],
      status: manufacturingOutputStatus(boardFiles.manufacturing_output),
      action: (
        <Button
          label={
            boardFiles.manufacturing_output.file === null
              ? "Generate"
              : "Regenerate"
          }
          onClick={() => {
            void createManufacturingOutput();
          }}
          variant={
            isGeneratingManufacturingOutput
              ? ButtonVariants.SECONDARY
              : ButtonVariants.PRIMARY
          }
        />
      ),
    },
    {
      title: "Shopping list",
      rows:
        shoppingListVersions.length > 0
          ? shoppingListVersions
          : [{ key: "shopping-list", download: null }],
      status: isShoppingListProcessing ? (
        <LoadingIcon text={"Processing..."} />
      ) : (
        boardFiles.part_shopping_lists?.status
      ),
      action: (
        <Button
          label={"Refresh"}
          onClick={() => {
            void updateShoppingList(board.id);
          }}
          variant={
            isShoppingListProcessing
              ? ButtonVariants.SECONDARY
              : ButtonVariants.PRIMARY
          }
        />
      ),
    },
  ];

  if (boardHasBeenPaidFor(board)) {
    groups.push(
      {
        title: "BOM",
        rows: [
          {
            key: "bom",
            download: (
              <FakeLink
                onClick={() => {
                  void downloadBOM(board);
                }}
              >
                Download
              </FakeLink>
            ),
          },
        ],
      },
      {
        title: "Pick list",
        rows: [
          {
            key: "pick-list",
            download: (
              <FakeLink
                onClick={() => {
                  void downloadPartPickList(
                    board.id,
                    board.project_name,
                    shipmentId,
                  );
                }}
              >
                Download
              </FakeLink>
            ),
          },
        ],
      },
    );
  }
  if (board.order_summary !== null) {
    groups.push({
      title: "Traveler",
      rows: [
        {
          key: "traveler",
          download: (
            <FakeLink
              onClick={() => {
                void downloadTravelerSticker(board.id);
              }}
            >
              Download
            </FakeLink>
          ),
        },
      ],
    });
  }

  const visibleGroups = groups.filter((group) => {
    return group.rows.length > 0;
  });

  return (
    <>
      <LoadingOverlay loading={updating} text={"Updating..."} />
      <FilesTable>
        <thead>
          <TableHeaderRow>
            <TableHeaderCell width={"16%"}>File</TableHeaderCell>
            <TableHeaderCell width={"28%"}>Download</TableHeaderCell>
            <TableHeaderCell width={"16%"}>Uploaded</TableHeaderCell>
            <TableHeaderCell width={"12%"}>By</TableHeaderCell>
            <TableHeaderCell width={"16%"}>Status</TableHeaderCell>
            <TableHeaderCell width={"12%"} />
          </TableHeaderRow>
        </thead>
        <tbody>
          {visibleGroups.map((group, groupIndex) => {
            const shaded = groupIndex % 2 === 1;
            const additional = group.additional ?? [];
            const collapsible =
              additional.length > MAX_UNCOLLAPSED_ADDITIONAL_FILES;
            const expanded = expandedGroups.has(group.title);
            const fileRow = (
              version: FileVersion,
              label: ReactNode,
              isAdditional: boolean,
              isFirst: boolean,
            ) => {
              return (
                <FileRow
                  key={`${group.title}-${version.key}`}
                  $shaded={shaded}
                  $previous={isAdditional}
                >
                  <FileCell>{label}</FileCell>
                  <FileCell>{version.download ?? "-"}</FileCell>
                  <FileCell>
                    {version.updatedAt
                      ? datetimeStringToHumanReadableDatetime(version.updatedAt)
                      : null}
                  </FileCell>
                  <FileCell>
                    {version.updatedBy && (
                      <UserIdPill userId={version.updatedBy} />
                    )}
                  </FileCell>
                  <FileCell>{isFirst && group.status}</FileCell>
                  <FileCell>{isFirst && group.action}</FileCell>
                </FileRow>
              );
            };
            return [
              ...group.rows.map((version, index) => {
                return fileRow(
                  version,
                  index === 0 ? group.title : null,
                  false,
                  index === 0,
                );
              }),
              collapsible && (
                <FileRow
                  key={`${group.title}-toggle`}
                  $shaded={shaded}
                  $previous
                >
                  <FileCell />
                  <FileCell colSpan={5}>
                    <ExpandToggle
                      type="button"
                      aria-expanded={expanded}
                      onClick={() => {
                        toggleGroup(group.title);
                      }}
                    >
                      <Chevron $expanded={expanded} />
                      {expanded ? "Hide " : "Show "}
                      {additional.length}{" "}
                      {(group.additionalLabel ?? "Previous").toLowerCase()}
                    </ExpandToggle>
                  </FileCell>
                </FileRow>
              ),
              ...(!collapsible || expanded
                ? additional.map((version) => {
                    return fileRow(
                      version,
                      group.additionalLabel ?? "Previous",
                      true,
                      false,
                    );
                  })
                : []),
            ];
          })}
        </tbody>
      </FilesTable>
      <PanelDetails board={board} />
    </>
  );
}
